/**
 * web/src/screens/Notebook/hooks/index.ts — useNotebookState, the state and
 * data access the Notebook screen owns (card vncVrGk1hc9i: round two of the
 * split). The seams of the former hooks.ts now live in one module each under
 * ./hooks/ (feed, capture, dates, edit-detail, clusters, opinions, share,
 * context, filters, derive); this composition root wires them together and
 * keeps the effect spine below in its original declaration order, so every
 * effect keeps its original dependency array and its position relative to the
 * other effects. It returns the same state object the screen always passed
 * down to its section components as props.
 */
import { useEffect, useState } from 'react';
import { session } from '../../../shell/bridge';
import { loadMembers } from '../../../lib/api';
import { markHintSeen } from '../../../lib/hints';
import { shareCapture, type PearShare } from '../../../lib/shareTarget';
import { partnerNameOf, type EntryType, type NotebookComposeIntent } from '../types';
import { useNotebookFeed } from './feed';
import { useNotebookDates } from './dates';
import { useNotebookShare } from './share';
import { useNotebookCapture } from './capture';
import { useNotebookEditDetail } from './edit-detail';
import { useNotebookClusters } from './clusters';
import { useNotebookOpinions } from './opinions';
import { useNotebookContext } from './context';
import { useNotebookFilters } from './filters';
import { deriveListView } from './derive';

export function useNotebookState({ intent, requestedCollection, requestedItem, onItemConsumed, pendingShare, onShareConsumed, onOpenDates, giftAck }: {
  intent?: NotebookComposeIntent | null;
  /** Quick link from the More menu (card yOcdjnR6pXBz): land on this
   *  collection. null = no request. Does NOT open a composer — the link is
   *  a filter, not an add. */
  requestedCollection?: string | null;
  /** Card 5wr2rXdlCSTh: the item a Search result asked for. Opened once the
   *  feed can resolve it, then reported consumed. */
  requestedItem?: string | null;
  onItemConsumed?: () => void;
  /** A share from another app (PWA share_target, card jZn7vpkB7EG1), parked
   *  until this screen could hold it. null = nothing parked. */
  pendingShare?: PearShare | null;
  /** Called once the parked share has been applied — the payload is dropped. */
  onShareConsumed?: () => void;
  /** Card anKalYfYvokw: the date footnote used to describe the route to
   *  Us → Dates; this opens it. App owns navigation, so the screen asks. */
  onOpenDates?: () => void;
  /** The gift acknowledgement line. The literal itself stays in index.tsx
     because scripts/probe-gift-enrich.mjs reads that file for it; the screen
     hands it down here and to the card renderer. */
  giftAck: string;
}) {
  const me = session();
  /* Card SApyMHlbeyhg — the cached members list, so a note's byline can name
     its author. The same localStorage cache Chores reads (no new fetch, no App
     plumbing); recomputed each render is a cheap synchronous read. */
  const members = loadMembers();
  /* Card DfhelObKj9iP (Jonathan 2026-10-30, "named in direct UI, generic
     partner in docs"): the visibility copy on this screen is read by a paired
     device, so it names her. `partnerNameOf` already owns the fallback for the
     fresh-pair case where the cache holds only this member. */
  const partnerLabel = partnerNameOf(members, me?.memberId ?? null);
  const partnerLabelCap = partnerLabel === 'your partner' ? 'Your partner' : partnerLabel;

  const feed = useNotebookFeed({ me });
  const dates = useNotebookDates({ busyId: feed.busyId, setBusyId: feed.setBusyId });
  const share = useNotebookShare({
    busyId: feed.busyId, setBusyId: feed.setBusyId, reload: feed.reload,
    partnerLabel, partnerLabelCap,
  });
  const capture = useNotebookCapture({
    reload: feed.reload, reloadRef: feed.reloadRef,
    loadDates: dates.loadDates, setDateSaved: dates.setDateSaved,
    draftSharedFor: share.draftSharedFor, setSharedDraft: share.setSharedDraft, setGiftDraft: share.setGiftDraft,
    giftAck,
  });
  const edit = useNotebookEditDetail({ reload: feed.reload, enrichPasses: capture.enrichPasses });
  const clusterSeam = useNotebookClusters({ me, loadDates: dates.loadDates, setDateSaved: dates.setDateSaved });
  const opinions = useNotebookOpinions({
    busyId: feed.busyId, setBusyId: feed.setBusyId, reload: feed.reload, setRowError: feed.setRowError, flashRowError: feed.flashRowError,
  });
  const ctx = useNotebookContext();
  const filters = useNotebookFilters({
    contextLive: ctx.contextLive, contextId: ctx.contextId,
    setContextId: ctx.setContextId, setFeedRoving: ctx.setFeedRoving,
  });

  const {
    items, setItems, failed, setFailed, busyId, setBusyId,
    tags, setTags, suggestions, setSuggestions,
    rowError, setRowError, rowErrorTimer,
    removedItem, setRemovedItem, undoTimer,
    reloadSeq, reloadRef, searchRef, reload,
    flashRowError, toggleDone, deleteEntry, undoRemove, canRemove,
    pendingRemove, cancelRemove, confirmRemove,
  } = feed;
  const { dateIdeas, setDateIdeas, dateSaved, setDateSaved, loadDates, sendDate } = dates;
  const { sharedDraft, setSharedDraft, giftDraft, setGiftDraft, draftSharedFor, shareEntry, makePrivate } = share;
  const {
    draft, setDraft, adding, setAdding,
    quickType, setQuickType, typeMenuOpen, setTypeMenuOpen,
    collection, setCollection, justSavedId, setJustSavedId,
    captureToast, setCaptureToast, captureToastTimer,
    receipt, setReceipt,
    quickSaved, setQuickSaved, quickSavedTimer,
    enrichPasses, captureError, setCaptureError, quickError, setQuickError,
    linkTab, detectedType, captureType, pickPill, clearCapture,
    captureOpen, setCaptureOpenState, captureOpenRef, captureOpenedAt,
    setCaptureOpen, openCapture, closeCaptureNow, dismissCapture,
    resetQuickField, flashQuickSaved, flashCaptureToast, undoCapture, captureKeyDown,
    captureSave, trackFiling, mutateReceipt, undoReceipt, flipReceiptScope,
  } = capture;
  const {
    editingId, setEditingId, editTitle, setEditTitle, editText, setEditText,
    editBusy, setEditBusy, editError, setEditError, editTextRef,
    detailId, setDetailId, detailDate, setDetailDate, detailPlace, setDetailPlace,
    detailBusy, setDetailBusy, detailError, setDetailError,
    placeQuery, setPlaceQuery, placeBusy, setPlaceBusy,
    placeResults, setPlaceResults, placeNote, setPlaceNote,
    startEdit, cancelEdit, saveEdit,
    openDetail, closeDetail, searchPlaces, saveDetail, savePlace,
  } = edit;
  const {
    clusterPrefs, setClusterPrefs, openCluster, setOpenCluster,
    clusterAddPick, setClusterAddPick, writeClusterPrefs,
    planCluster, pickClusterDates, dissolveCluster, removeClusterMember, addClusterMember, restoreClusters,
  } = clusterSeam;
  const {
    busySugg, setBusySugg, overriding, setOverriding, overrideText, setOverrideText,
    resolveSuggestion, fileKind, retag,
  } = opinions;
  const {
    contextId, setContextId, feedRoving, setFeedRoving,
    viewportW, setViewportW, contextLive, contextInline,
    viewMode, setViewMode, pickView,
  } = ctx;
  const {
    kindFilter, setKindFilter, subtypeFilter, setSubtypeFilter, statusFilter, setStatusFilter,
    search, setSearch, filtersOpen, setFiltersOpen,
    detailsOpen, setDetailsOpen, suggsOpen, setSuggsOpen,
    toggleId, openDetails, activeFilterCount, clearFilters, feedKeyDown,
  } = filters;

  /* Card HabrLFaV01Ty: the card a Search result brought you to. Held for the
     visit — it is the "you are here" marker over the feed the result landed
     in, and it clears when the panel unmounts like every other screen state. */
  const [foundId, setFoundId] = useState<string | null>(null);
  useEffect(() => {
    const el = editTextRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [editText, editingId]);

  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /* PRD P1 — Esc closes the open card. Window-level and in the capture phase
     so it works from anywhere on the surface, and it yields to any dialog
     already up (the More drawer, Settings, the tour — the shared Overlay
     primitive renders every one of them as role="dialog"): Escape belongs to
     whichever surface the user is actually looking at, never to both. */
  useEffect(() => {
    if (!contextId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('[role="dialog"]')) return;
      setContextId(null);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [contextId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!captureOpenRef.current) return;
      if (!document.querySelector('[data-testid="nb-capture-sheet"]')) return;
      if (document.querySelector('.pear-tooltip-bubble, .nb-type-menu')) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      clearCapture();
      setCaptureOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [clearCapture, setCaptureOpen]);

  useEffect(() => {
    void loadDates();
  }, [loadDates]);

  // More-menu quick link: narrow to that collection without pinning a capture
  // type. Landing here is a fresh arrival, so the bar goes back to auto-detect
  // instead of keeping whatever type the previous tab had chosen
  // (card quWhYyrNiIbt — the chip is state, and a filter door must not leave
  // it naming a type the person did not pick on this visit).
  useEffect(() => {
    if (!requestedCollection) return;
    setCollection(requestedCollection as 'all' | EntryType);
    setQuickType(null);
    setTypeMenuOpen(false);
  }, [requestedCollection]);

  /* Card 5wr2rXdlCSTh — a Search result opens the item it names. Waits for the
     feed so the id resolves to a row, then opens it where this viewport shows
     detail: the third-zone context panel on a desktop, the detail sheet on a
     phone (below 760px there is no third zone at all). The request is one-shot
     — it is reported consumed, so coming back to Notebook does not reopen it.

     Card HabrLFaV01Ty (Jonathan email 2026-11-04) — that landing was wrong in
     both places he looked. On a desktop the item "opened" in the third-zone
     context panel, which reads as a sidebar, so the main pane never changed
     and the eye never moved; on mobile web nothing was scrolled or shown, so a
     tap landed on the top of the feed and looked like Notebook's default
     page. Now the hit is brought INTO the feed on both viewports: the
     collection filter goes back to every type (a hit from another collection
     was not in the rendered list at all), the card is scrolled to the middle
     of the pane, focused, and marked with `is-found` for this visit; where the
     viewer owns the card its own inline detail form opens too. The context
     panel is no longer the destination — a phone has no third zone, so the two
     viewports now land identically. */
  useEffect(() => {
    if (!requestedItem || items === null) return;
    const item = items.find(i => i.itemId === requestedItem);
    if (item) {
      setCollection('all');
      setQuickType(null);
      setTypeMenuOpen(false);
      setFoundId(item.itemId);
      setFeedRoving(item.itemId);
      /* Ownership has to be read SYNCHRONOUSLY here: `canRemove` waits on the
         members fetch, and this effect runs the moment the feed lands — with
         `me` still null it answered false and the hit arrived with no detail
         open at all. `session()` already carries this device's member id. */
      const viewerId = me?.memberId ?? session()?.memberId ?? null;
      if (viewerId && item.addedBy === viewerId) openDetail(item);
      /* The row only exists after this render commits — and again after the
         collection reset re-renders the list, hence the double frame. */
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const el = document.querySelector(`[data-context-card="${item.itemId}"]`) as HTMLElement | null;
        if (!el) return;
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        el.focus({ preventScroll: true });
      }));
    }
    if (item || items.length > 0) onItemConsumed?.();
  }, [requestedItem, items, me, setCollection, setQuickType, setTypeMenuOpen, setFeedRoving, openDetail, onItemConsumed]);

  useEffect(() => {
    reloadRef.current = reload;
    void reload();
    // Unfurl finishes after the response lands — poll twice so a fresh paste
    // fills in without the partner having to pull-to-refresh.
    const t1 = window.setTimeout(() => { void reload(); }, 2500);
    const t2 = window.setTimeout(() => { void reload(); }, 6000);
    return () => { window.clearTimeout(t1); window.clearTimeout(t2); };
  }, [reload]);

  /* PRD §6.2 C4 — the bar is reachable and usable from the keyboard, and a
     link can be captured from anywhere on the page:

       N        focus the bar (only when no field already has the keystroke)
       Enter    save (the bar's Composer passes enterBehavior='submit';
                Shift+Enter still breaks the line)
       Esc      clear (captureKeyDown on the form)
       paste    a URL pasted OUTSIDE an input starts a capture there and then
  */
  useEffect(() => {
    const focusBar = () => {
      /* The bar can be closed (card ANrPXTBtF2B6): N — and a URL pasted
         anywhere on the page — opens it again rather than doing nothing. */
      if (!captureOpenRef.current) { openCapture(); return; }
      const el = document.getElementById('nb-quick') as HTMLTextAreaElement | null;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      const typing = !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (typing) return;
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        focusBar();
      }
    };
    const onPaste = (e: ClipboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const text = e.clipboardData?.getData('text')?.trim() ?? '';
      if (!text || !/https?:\/\//i.test(text)) return;
      e.preventDefault();
      setDraft(text);
      setQuickType(null); // a pasted link decides its own type
      setQuickSaved(null);
      window.setTimeout(focusBar, 0);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('paste', onPaste);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('paste', onPaste);
    };
  }, [openCapture]);

  // Gift enrichment (card OviOTaNsNivQ): the search runs AFTER the save has
  // answered, so a fresh gift card sits at 'pending' until a refetch sees the
  // detail land. Same cadence as the unfurl polls above, capped at eight
  // passes so a stalled engine cannot turn into an infinite loop of GETs.
  const pendingGift = (items ?? []).some((i) => i.entryType === 'gift' && i.enrichStatus === 'pending');
  useEffect(() => {
    if (!pendingGift || enrichPasses.current >= 8) return;
    enrichPasses.current += 1;
    const t = window.setTimeout(() => { void reload(); }, 3500);
    return () => window.clearTimeout(t);
  }, [pendingGift, reload, items]);

  // First-move hint (Rl0vzkZ2hdAe): the paste-a-link line has served its turn
  // the moment there is anything in the notebook — remember it per couple.
  useEffect(() => {
    if (items && items.length > 0) markHintSeen('notebook');
  }, [items]);

  // Compose menu deep-link (card RvESgw7lVASX): a notebook tile lands here with
  // its type already chosen. The capture bar is shared, so the intent narrows
  // the collection AND points the bar's chip at that type (card quWhYyrNiIbt:
  // 'Link' has no separate form any more — the bar takes a URL itself and
  // auto-detects it).
  useEffect(() => {
    if (!intent) return;
    setCollection(intent.kind);
    setQuickType(intent.kind);
    setQuickSaved(null);
    /* A compose-menu deep link is a capture intent, so it reopens a bar the
       person had closed (card ANrPXTBtF2B6). */
    if (!captureOpenRef.current) setCaptureOpen(true);
    window.setTimeout(() => {
      (document.getElementById('nb-quick') as HTMLTextAreaElement | null)?.focus();
    }, 0);
  }, [intent, setCaptureOpen]);

  /* PWA share target (card jZn7vpkB7EG1): a share parked by main.tsx fills
     the bar and opens it. Words only — no fetch, no POST: Save is still the
     tap that writes to the couple's data. The payload is dropped as soon as
     it has been applied, so a tab away and back cannot re-fill a bar the
     person already dismissed. Type is pinned by shareCapture (a URL that
     parses → link, a url that will not → plain note, none → the bar's own
     detection). */
  useEffect(() => {
    if (!pendingShare) return;
    const { body, type } = shareCapture(pendingShare);
    if (body) {
      setDraft(body);
      setQuickType(type);
      setQuickSaved(null);
      if (!captureOpenRef.current) setCaptureOpen(true);
      window.setTimeout(() => {
        (document.getElementById('nb-quick') as HTMLTextAreaElement | null)?.focus();
      }, 0);
    }
    onShareConsumed?.();
  }, [pendingShare, onShareConsumed, setCaptureOpen]);

  useEffect(() => {
    searchRef.current = linkTab ? search : '';
    const t = window.setTimeout(() => { void reload(); }, 200);
    return () => { window.clearTimeout(t); };
  }, [search, linkTab, reload]);

  const {
    linkItems, chipLinks, kindChips, subtypeTabs, visible,
    clusters, clusterPrefsDirty, rovingCardId, counterBase,
  } = deriveListView({
    items, collection, kindFilter, subtypeFilter, statusFilter,
    tags, clusterPrefs, contextLive, feedRoving,
  });
  return {
    intent,
    requestedCollection,
    pendingShare,
    onShareConsumed,
    onOpenDates,
    giftAck,
    me,
    members,
    partnerLabel,
    partnerLabelCap,
    items,
    setItems,
    failed,
    setFailed,
    draft,
    setDraft,
    adding,
    setAdding,
    busyId,
    setBusyId,
    quickType,
    setQuickType,
    typeMenuOpen,
    setTypeMenuOpen,
    collection,
    setCollection,
    justSavedId,
    setJustSavedId,
    captureToast,
    setCaptureToast,
    captureToastTimer,
    sharedDraft,
    setSharedDraft,
    giftDraft,
    setGiftDraft,
    draftSharedFor,
    editingId,
    setEditingId,
    editTitle,
    setEditTitle,
    editText,
    setEditText,
    editBusy,
    setEditBusy,
    editError,
    setEditError,
    editTextRef,
    detailId,
    setDetailId,
    detailDate,
    setDetailDate,
    detailPlace,
    setDetailPlace,
    detailBusy,
    setDetailBusy,
    detailError,
    setDetailError,
    placeQuery,
    setPlaceQuery,
    placeBusy,
    setPlaceBusy,
    placeResults,
    setPlaceResults,
    placeNote,
    setPlaceNote,
    receipt,
    setReceipt,
    tags,
    setTags,
    suggestions,
    setSuggestions,
    busySugg,
    setBusySugg,
    overriding,
    setOverriding,
    overrideText,
    setOverrideText,
    dateIdeas,
    setDateIdeas,
    dateSaved,
    setDateSaved,
    quickSaved,
    setQuickSaved,
    quickSavedTimer,
    enrichPasses,
    captureError,
    setCaptureError,
    quickError,
    setQuickError,
    rowError,
    setRowError,
    rowErrorTimer,
    removedItem,
    setRemovedItem,
    undoTimer,
    reloadSeq,
    reloadRef,
    contextId,
    setContextId,
    foundId,
    feedRoving,
    setFeedRoving,
    viewportW,
    setViewportW,
    contextLive,
    contextInline,
    viewMode,
    setViewMode,
    pickView,
    clusterPrefs,
    setClusterPrefs,
    openCluster,
    setOpenCluster,
    clusterAddPick,
    setClusterAddPick,
    writeClusterPrefs,
    linkTab,
    detectedType,
    captureType,
    pickPill,
    clearCapture,
    captureOpen,
    setCaptureOpenState,
    captureOpenRef,
    captureOpenedAt,
    setCaptureOpen,
    openCapture,
    closeCaptureNow,
    dismissCapture,
    resetQuickField,
    flashQuickSaved,
    flashCaptureToast,
    undoCapture,
    captureKeyDown,
    loadDates,
    reload,
    pendingGift,
    captureSave,
    trackFiling,
    mutateReceipt,
    undoReceipt,
    flipReceiptScope,
    flashRowError,
    toggleDone,
    deleteEntry,
    undoRemove,
    canRemove,
    pendingRemove,
    cancelRemove,
    confirmRemove,
    startEdit,
    cancelEdit,
    saveEdit,
    openDetail,
    closeDetail,
    searchPlaces,
    saveDetail,
    savePlace,
    planCluster,
    pickClusterDates,
    dissolveCluster,
    removeClusterMember,
    addClusterMember,
    restoreClusters,
    shareEntry,
    makePrivate,
    fileKind,
    retag,
    resolveSuggestion,
    sendDate,
    kindFilter,
    setKindFilter,
    subtypeFilter,
    setSubtypeFilter,
    statusFilter,
    setStatusFilter,
    search,
    setSearch,
    filtersOpen,
    setFiltersOpen,
    detailsOpen,
    setDetailsOpen,
    suggsOpen,
    setSuggsOpen,
    toggleId,
    openDetails,
    activeFilterCount,
    clearFilters,
    feedKeyDown,
    linkItems,
    chipLinks,
    kindChips,
    subtypeTabs,
    visible,
    clusters,
    clusterPrefsDirty,
    rovingCardId,
    counterBase,
  };
}

export type NotebookState = ReturnType<typeof useNotebookState>;
